import { Star } from "lucide-react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Section } from "@/components/layout/Section";
import { Container } from "@/components/layout/Container";
import { AnimatedSection } from "@/components/ui/animated-section";
import { staggerContainer, staggerItem } from "@/lib/animations";

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  company: string;
}

const testimonials: Testimonial[] = [
  {
    quote: "SurviAI transformed how our research team handles survey creation. What used to take days now takes minutes.",
    author: "Dr. Sarah Chen",
    role: "Research Director",
    company: "Stanford University",
  },
  {
    quote: "The AI accuracy is impressive. It correctly identified all our question types and even suggested improvements.",
    author: "Michael Torres",
    role: "Product Manager",
    company: "TechCorp Inc.",
  },
  {
    quote: "Finally, a tool that understands the complexity of academic surveys. The logic builder is a game-changer.",
    author: "Dr. Emily Watson",
    role: "Senior Analyst",
    company: "Global Research Partners",
  },
];

export function Testimonials() {
  return (
    <Section>
      <Container>
        <AnimatedSection className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            Loved by <span className="text-gradient">Research Teams</span>
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            See what our customers have to say
          </p>
        </AnimatedSection>
        
        {/* Responsive grid with stagger animation */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {testimonials.map((testimonial, index) => (
            <motion.div key={index} variants={staggerItem}>
              <Card className="hover-lift h-full">
                <CardContent className="pt-6">
                  {/* Star rating */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-warning text-warning" />
                    ))}
                  </div>
                  
                  {/* Quote */}
                  <p className="text-foreground mb-6 leading-relaxed">"{testimonial.quote}"</p>
                  
                  {/* Author */}
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {testimonial.author[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-foreground">{testimonial.author}</p>
                      <p className="text-sm text-muted-foreground">
                        {testimonial.role}, {testimonial.company}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </Container>
    </Section>
  );
}
